import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import SEOHead from "@/components/SEOHead";
import { ArrowLeft, ArrowRight, Check, Users } from "lucide-react";
import KaspiPaymentStep from "@/components/KaspiPaymentStep";
import { WorkingHoursInline } from "@/components/WorkingHours";
import { useLanguage } from "@/lib/LanguageContext";
import api, { formatPrice } from "@/lib/api";

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, l, lang } = useLanguage();
  const [step, setStep] = useState(0);
  const [houses, setHouses] = useState([]);
  const [availability, setAvailability] = useState({ available_hours: [], working_hours: [] });
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [schedule, setSchedule] = useState(null);

  const STEPS = [t("booking.step1"), t("booking.step2"), t("booking.step3"), t("booking.step4"), t("booking.step5")];

  const [form, setForm] = useState({
    house_id: searchParams.get("house") || "",
    date: null,
    start_hour: null,
    duration: 1,
    client_name: "",
    client_phone: "",
    notes: "",
  });

  const selectedHouse = houses.find((h) => h.id === form.house_id);

  useEffect(() => {
    api.get("/houses").then((r) => setHouses(r.data)).catch(() => {});
    api.get("/schedule").then((r) => setSchedule(r.data.days)).catch(() => {});
    if (searchParams.get("house")) setStep(1);
  }, [searchParams]);

  // JS getDay(): 0=Sunday, backend dow: 0=Monday
  const dayForDate = (date) => (schedule ? schedule[(date.getDay() + 6) % 7] : null);
  const isClosedDay = (date) => !!dayForDate(date)?.closed;

  useEffect(() => {
    if (form.house_id && form.date) {
      const dateStr = form.date.toISOString().split("T")[0];
      api.get(`/availability/${form.house_id}?date=${dateStr}`)
        .then((r) => setAvailability(r.data))
        .catch(() => setAvailability({ available_hours: [], working_hours: [] }));
    }
  }, [form.house_id, form.date]);

  const canProceed = () => {
    if (step === 0) return !!form.house_id;
    if (step === 1) return !!form.date;
    if (step === 2) return form.start_hour !== null && form.duration >= 1;
    if (step === 3) return form.client_name.trim() && form.client_phone.trim();
    return true;
  };

  const calculateTotal = () => {
    return (selectedHouse?.price_per_hour || 0) * form.duration;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const utm = JSON.parse(sessionStorage.getItem("utm_data") || "{}");
    try {
      const dateStr = form.date.toISOString().split("T")[0];
      const r = await api.post("/bookings", {
        house_id: form.house_id, date: dateStr, start_hour: form.start_hour,
        duration: form.duration, services: [],
        client_name: form.client_name, client_phone: form.client_phone, notes: form.notes,
        utm_source: utm.source || "", utm_medium: utm.medium || "", utm_campaign: utm.campaign || "",
      });
      setPayment(r.data.payment);
      setStep(5);
    } catch (e) {
      alert(e.response?.data?.detail || t("booking.error"));
    } finally {
      setLoading(false);
    }
  };

  const canBookSlot = (hour) => {
    for (let i = 0; i < form.duration; i++) {
      if (!availability.available_hours?.includes((hour + i) % 24)) return false;
    }
    return true;
  };

  const dateLang = lang === "kz" ? "kk-KZ" : "ru-RU";

  if (step === 5) {
    return (
      <div className="py-24 md:py-32" data-testid="booking-success">
        <div className="max-w-lg mx-auto px-4">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-serif text-3xl mb-6 text-center">{t("booking.success")}</h2>
          <KaspiPaymentStep payment={payment} />
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32" data-testid="booking-page">
      <SEOHead path="/booking" />
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <h1 className="text-4xl md:text-5xl font-serif tracking-tight mb-8">{t("booking.title")}</h1>

        {/* Steps indicator */}
        <div className="flex items-center gap-1 mb-10 overflow-x-auto pb-2" data-testid="booking-steps">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center">
              <div className={`px-3 py-1 text-xs font-sans tracking-wide whitespace-nowrap ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"}`}>
                {i + 1}. {s}
              </div>
              {i < STEPS.length - 1 && <div className="w-4 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 0: Select House */}
        {step === 0 && (
          <div data-testid="step-house">
            <h2 className="font-serif text-2xl mb-6">{t("booking.selectHouse")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {houses.map((h) => (
                <button
                  key={h.id}
                  onClick={() => setForm({ ...form, house_id: h.id })}
                  className={`text-left p-4 border transition-all ${form.house_id === h.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/20"}`}
                  data-testid={`select-house-${h.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-primary uppercase tracking-widest">{t(`houses.${h.house_type}`)}</span>
                    <span className="text-xs text-muted-foreground"><Users className="w-3 h-3 inline" /> {h.capacity_max}</span>
                  </div>
                  <h3 className="font-serif text-lg">{l(h)}</h3>
                  <p className="text-primary font-semibold font-sans text-sm mt-1">{formatPrice(h.price_per_hour)} {t("common.perHour")}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Select Date */}
        {step === 1 && (
          <div data-testid="step-date">
            <h2 className="font-serif text-2xl mb-6">{t("booking.selectDate")}</h2>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(d) => setForm({ ...form, date: d, start_hour: null })}
                disabled={[{ before: new Date() }, isClosedDay]}
                className="bg-card border border-border p-4"
                data-testid="booking-calendar"
              />
            </div>
          </div>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && (
          <div data-testid="step-time">
            <h2 className="font-serif text-2xl mb-4">{t("booking.selectTime")}</h2>
            {form.date && <WorkingHoursInline day={dayForDate(form.date)} />}
            <div className="mb-6">
              <label className="text-sm text-muted-foreground mb-2 block">{t("booking.duration")}</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((d) => (
                  <button
                    key={d}
                    onClick={() => setForm({ ...form, duration: d, start_hour: null })}
                    className={`px-5 py-2 text-sm border ${form.duration === d ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/20"}`}
                    data-testid={`duration-${d}`}
                  >
                    {d} {t("common.hour")}
                  </button>
                ))}
              </div>
            </div>
            <label className="text-sm text-muted-foreground mb-2 block">{t("booking.startTime")}</label>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {(availability.working_hours?.length ? availability.working_hours : []).map((h) => {
                const available = canBookSlot(h);
                return (
                  <button
                    key={h}
                    onClick={() => available && setForm({ ...form, start_hour: h })}
                    disabled={!available}
                    className={`py-3 text-sm text-center border transition-all ${
                      form.start_hour === h ? "border-primary bg-primary text-primary-foreground" :
                      available ? "border-border text-foreground hover:border-primary/20" :
                      "border-border text-muted-foreground/30 cursor-not-allowed"
                    }`}
                    data-testid={`time-slot-${h}`}
                  >
                    {h < 10 ? `0${h}` : h}:00
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <div data-testid="step-contact">
            <h2 className="font-serif text-2xl mb-6">{t("booking.contactInfo")}</h2>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.yourName")} *</label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder={t("common.name")}
                  className="bg-transparent border-border rounded-none py-5 focus:border-primary"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.yourPhone")} *</label>
                <Input
                  value={form.client_phone}
                  onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                  placeholder="+7 7XX XXX XX XX"
                  className="bg-transparent border-border rounded-none py-5 focus:border-primary"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("booking.notes")}</label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={t("common.description")}
                  className="bg-transparent border-border rounded-none py-5 focus:border-primary"
                  data-testid="input-notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Summary */}
        {step === 4 && (
          <div data-testid="step-summary">
            <h2 className="font-serif text-2xl mb-6">{t("booking.summary")}</h2>
            <div className="bg-card border border-border p-6 space-y-4">
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("booking.step1")}</span><span className="text-sm">{selectedHouse && l(selectedHouse)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("common.date")}</span><span className="text-sm">{form.date?.toLocaleDateString(dateLang)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("common.time")}</span><span className="text-sm">{form.start_hour < 10 ? `0${form.start_hour}` : form.start_hour}:00 — {((form.start_hour + form.duration) % 24) < 10 ? `0${(form.start_hour + form.duration) % 24}` : (form.start_hour + form.duration) % 24}:00 ({form.duration} {t("common.hour")})</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("booking.houseCost")}</span><span className="text-sm">{formatPrice(calculateTotal())}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("common.name")}</span><span className="text-sm">{form.client_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground text-sm">{t("common.phone")}</span><span className="text-sm">{form.client_phone}</span></div>
              <div className="border-t border-border pt-4 flex justify-between items-center">
                <span className="font-serif text-xl">{t("common.total")}</span>
                <span className="text-2xl text-primary font-serif">{formatPrice(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-muted-foreground gap-2" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </Button>
          ) : <div />}
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-primary text-primary-foreground rounded-none px-8 gap-2 hover:bg-primary/90" data-testid="btn-next">
              {t("common.next")} <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-primary text-primary-foreground rounded-none px-8 hover:bg-primary/90" data-testid="btn-submit-booking">
              {loading ? t("booking.submitting") : t("common.confirm")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
